import React, { useEffect, useState, useCallback } from "react";
import Loading from '../../Common/Loading';
import { useNavigate } from "react-router-dom";
import popimg from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../../Common/Images/ImagesForAlert/popup_close_n.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface Setting {
    Identifier: string;
    Contents: string;
    Description: string;
}

type AlertType = "success" | "error" | "info";

interface AlertState {
    message: string;
    type: AlertType;
}


interface AddEditModalProps {
    mode: "ADD" | "EDIT";
    initial: Partial<Setting>;
    onClose: (saved: boolean) => void;
}

const AddEditModal: React.FC<AddEditModalProps> = ({ mode, initial, onClose }) => {
    const [identifier, setIdentifier] = useState(initial.Identifier ?? "");
    const [contents, setContents] = useState(initial.Contents ?? "");
    const [description, setDescription] = useState(initial.Description ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();


    document.body.style.backgroundColor = "#CCC";

    const handleSave = async () => {
        if (!identifier.trim()) {
            setError("Identifier is required.");
            return;
        }
        setSaving(true);
        try {
            const method = mode === "ADD" ? "POST" : "PUT";
            const res = await fetch(`${API_BASE_URL}api/el-settings/`, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier, contents, description }),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                onClose(true);
            } else {
                setError(data.error ?? "Save failed.");
            }
        } catch {
            setError("An error occurred.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>

            <div style={modalStyles.overlay}>
                <div style={modalStyles.box}>
                    <div
                        style={{
                            backgroundImage: `url(${popimg}), url(${popbg})`,
                            backgroundRepeat: "no-repeat, no-repeat",
                            backgroundPosition: "left center, right center",
                            backgroundSize: "30% 100%, 70% 100%",
                            height: "16px",
                            color: "white",
                            padding: "7px 0px",
                            fontWeight: "bold",
                            fontSize: "12pt",
                            fontFamily: "arial",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                        }}
                    >
                        <span style={{ marginLeft: "10px" }}>
                        </span>
                        <img
                            src={popclose}
                            alt="close"
                            height="30px"
                            style={{ cursor: "pointer" }}
                            onClick={() => onClose(false)} />
                    </div>
                    <h2 style={modalStyles.title}>
                        {mode === "ADD" ? "ADD SETTING" : "EDIT SETTING"}
                    </h2>

                    {error && <div style={modalStyles.error}>{error}</div>}

                    <div style={{ ...modalStyles.field, padding: " 0px 20px" }}>
                        <label style={modalStyles.label}>Identifier:</label>
                        <input
                            style={modalStyles.input}
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            disabled={mode === "EDIT"} />
                    </div>
                    <div style={{ ...modalStyles.field, padding: "0px 20px" }}>
                        <label style={modalStyles.label}>Contents:</label>
                        <input
                            style={modalStyles.input}
                            value={contents}
                            onChange={(e) => setContents(e.target.value)} />
                    </div>
                    <div style={{ ...modalStyles.field, padding: "0px 20px" }}>
                        <label style={modalStyles.label}>Description:</label>
                        <input
                            style={modalStyles.input}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)} />
                    </div>

                    <div style={{ ...modalStyles.btnRow, padding: "20px" }}>
                        <button
                            style={{ ...modalStyles.btn, ...modalStyles.btnSave }}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                        <button
                            style={{ ...modalStyles.btn, ...modalStyles.btnCancel }}
                            onClick={() => onClose(false)}
                        >
                            Cancel
                        </button>
                    </div>

                </div>
            </div></>
    );
};


const EL_ManageSettings: React.FC = () => {
    const [settings, setSettings] = useState<Setting[]>([]);
    const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState<AlertState | null>(null);
    const [modal, setModal] = useState<{ mode: "ADD" | "EDIT" } | null>(null);
    const navigate = useNavigate();

    document.body.style.backgroundColor = "#fff"

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/el-settings/`);
            if (res.status === 401) {
                window.location.href = "/Common/Timeout";
                return;
            }
            const data = await res.json();
            setSettings(data.settings ?? []);
            setSelectedIdentifier(null);
        } catch {
            showAlert("Failed to load settings.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleAdd = () => {
        setModal({ mode: "ADD" });
    };

    const handleEdit = () => {
        if (!selectedIdentifier) {
            showAlert("Please Select a Row to Continue..", "info");
            return;
        }
        setModal({ mode: "EDIT" });
    };

    const handleDelete = async () => {
        if (!selectedIdentifier) {
            showAlert("Please Select a Row to Continue..", "info");
            return;
        }
        if (!window.confirm("Do you want to Delete the Selected Record?")) return;

        try {
            const res = await fetch(`${API_BASE_URL}api/el-settings/delete/`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier: selectedIdentifier }),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                showAlert("Setting deleted successfully.", "success");
                fetchSettings(); // replicates PopulateSettings() after delete
            } else {
                showAlert(data.error ?? "Delete failed.", "error");
            }
        } catch {
            showAlert("An error occurred during delete.", "error");
        }
    };

    const handleClose = () => {
        window.location.href = "/SysAdmin"
    };

    const handleModalClose = (saved: boolean) => {
        setModal(null);
        if (saved) fetchSettings();
    };

    const showAlert = (message: string, type: AlertType) => {
        setAlert({ message, type });
    };

    const selectedRow = settings.find((s) => s.Identifier === selectedIdentifier);

    return (
        <div style={styles.page} >
            <div
                style={{
                    backgroundImage: `url(${popimg}), url(${popbg})`,
                    backgroundRepeat: "no-repeat, no-repeat",
                    backgroundPosition: "left center, right center",
                    backgroundSize: "30% 100%, 70% 100%",
                    height: "16px",
                    color: "white",
                    padding: "7px 0px",
                    fontWeight: "bold",
                    fontSize: "12pt",
                    fontFamily: "arial",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                }}
            >
                <span style={{ marginLeft: "10px" }}>
                    System Settings
                </span>
                <img
                    src={popclose}
                    alt="close"
                    height="30px"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/SysAdmin")} />
            </div>
            {alert && (
                <div style={{ ...styles.alert, ...styles[`alert_${alert.type}`] }}>
                    <span>{alert.message}</span>
                    <button style={styles.alertClose} onClick={() => setAlert(null)}>✕</button>
                </div>
            )}

            <div style={styles.divFirst}>
                {loading ? (
                    <Loading />
                ) : (
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, ...styles.serialTh }}>#</th>
                                    <th style={styles.th}>Identifier</th>
                                    <th style={styles.th}>Contents</th>
                                    <th style={styles.th}>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {settings.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={styles.emptyCell}>No records found.</td>
                                    </tr>
                                ) : (
                                    settings.map((row, index) => (
                                        <tr
                                            key={row.Identifier}
                                            style={{
                                                ...styles.tr,
                                                ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                                                ...(selectedIdentifier === row.Identifier ? styles.trSelected : {}),
                                            }}
                                            onClick={() =>
                                                setSelectedIdentifier(
                                                    selectedIdentifier === row.Identifier ? null : row.Identifier
                                                )
                                            }
                                        >
                                            <td style={{ ...styles.td, ...styles.serialTd }}>
                                                {index + 1}
                                            </td>

                                            <td style={styles.td}>{row.Identifier}</td>
                                            <td
                                                style={{
                                                    ...styles.td,
                                                    padding: 0,
                                                    verticalAlign: "top"
                                                }}
                                            >
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: row.Contents || ""
                                                    }}
                                                />
                                            </td>
                                            <td style={styles.td}>{row.Description}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={styles.divControls}>
                <div style={styles.controlsInner}>
                    <button style={styles.btn} onClick={handleClose}>CLOSE</button>
                    <div style={styles.rightBtns}>
                        <button style={{ ...styles.btn, ...styles.btnGreen }} onClick={handleAdd}>Add</button>
                        <button style={{ ...styles.btn, ...styles.btnGreen }} onClick={handleEdit}>Edit</button>
                        <button style={{ ...styles.btn, ...styles.btnRed }} onClick={handleDelete}>Delete</button>
                    </div>
                </div>
            </div>

            {modal && (
                <AddEditModal
                    mode={modal.mode}
                    initial={modal.mode === "EDIT" && selectedRow ? selectedRow : {}}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};


const styles: Record<string, React.CSSProperties> = {
    page: {
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: "14px",
        margin: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#cccccc",
    },
    alert: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 14px",
        fontSize: "0.9em",
    },
    alert_success: { backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" },
    alert_error: { backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" },
    alert_info: { backgroundColor: "#d1ecf1", color: "#0c5460", border: "1px solid #bee5eb" },
    alertClose: { background: "none", border: "none", cursor: "pointer", fontSize: "1em", color: "inherit" },
    divFirst: {
        padding: "10px",
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    tableWrapper: {
        flex: 1,
        overflowY: "auto",
        border: "1px solid #a3c0e8",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontFamily: "Arial, Helvetica, sans-serif",
    },
    th: {
        backgroundColor: "#c7ddfd",
        color: "#2859ac",
        padding: "10px 10px",
        textAlign: "left",
        fontSize: "1em",
        fontWeight: "lighter",
        position: "sticky" as const,
        top: 0,
        zIndex: 1,
        borderRight: "1px solid #a3c0e8",
    },
    tr: {
        cursor: "pointer",
        borderRight: "1px solid #a3c0e8",
    },
    trSelected: {
        backgroundColor: "orange",
        color: "white"
    },
    td: {
        padding: "10px 10px",
        fontSize: "1em",
        verticalAlign: "middle",
        borderRight: "1px solid #a3c0e8",
    },
    serialTh: {
        width: "50px",
        textAlign: "center",
    },

    serialTd: {
        width: "50px",
        textAlign: "center",
    },
    emptyCell: {
        padding: "16px",
        textAlign: "center",
        color: "#888",
    },
    loadingText: {
        padding: "16px",
        textAlign: "center",
        color: "#666",
    },
    divControls: {
        padding: "10px",
        margin: "0 10px 10px 10px",
        borderTop: "1px solid #a3c0e8",
    },
    controlsInner: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    rightBtns: {
        display: "flex",
        gap: "10px",
    },
    btn: {
        padding: "3px 10px",
        fontSize: "0.85em",
        fontFamily: "Arial, Helvetica, sans-serif",
        border: "1px solid #000000",
        borderRadius: "2px",
        cursor: "pointer",
        backgroundColor: "#efefef",
        color: "#333",
    },
    btnGreen: {
        backgroundColor: "#efefef",
        color: "#000000",
        border: "1px solid #000000",
    },
    btnRed: {
        backgroundColor: "#efefef",
        color: "#000000",
        border: "1px solid #000000",
    },
    trEven: {
        backgroundColor: "#f4faff",
    },
    trOdd: {
        backgroundColor: "#fff",
    },
};

const modalStyles: Record<string, React.CSSProperties> = {
    overlay: {
        position: "fixed",
        inset: 0,
        backgroundColor: "#ebebeb52",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    },
    box: {
        backgroundColor: "azure",
        borderRadius: "4px",
        padding: "0px 0px",
        width: "450px",
        fontFamily: "Arial, Helvetica, sans-serif",
        boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
    },
    title: {
        fontSize: "1.2em",
        marginBottom: "16px",
        fontWeight: "bold",
        borderBottom: "1px solid #ddd",
        paddingBottom: "8px",
        textAlign: "center",
        textDecoration: "underline",
    },
    error: {
        backgroundColor: "#f8d7da",
        color: "#721c24",
        border: "1px solid #f5c6cb",
        padding: "6px 10px",
        borderRadius: "3px",
        fontSize: "0.88em",
        marginBottom: "12px",
    },
    field: {
        display: "flex",
        alignItems: "center",
        marginBottom: "12px",
    },
    label: {
        width: "100px",
        fontSize: "1.2em",
        color: "#444",
        flexShrink: 0,
    },
    input: {
        flex: 1,
        padding: "5px 8px",
        border: "1px solid #3f3f3f",
        borderRadius: "3px",
        fontSize: "0.88em",
        fontFamily: "Arial, Helvetica, sans-serif",
    },
    btnRow: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        marginTop: "16px",
        paddingTop: "12px",
        borderTop: "1px solid #eee",
    },
    btn: {
        padding: "6px 18px",
        fontSize: "0.85em",
        fontFamily: "Arial, Helvetica, sans-serif",
        border: "none",
        borderRadius: "3px",
        cursor: "pointer",
    },
    btnSave: {
        backgroundColor: "#f0f0f0",
        color: "#161616",
        border: "1px solid black"
    },
    btnCancel: {
        backgroundColor: "#f0f0f0",
        color: "#161616",
        border: "1px solid black"
    },
};

export default EL_ManageSettings;